import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { actions as termActions, selectors as termSelectors } from '../../../reducers/terms';
import isEqual from 'lodash/isEqual';
import ItemsDiffDisplay from './ItemsDiffDisplay';
import Loading from '../../Loading';

export const DiffStatus = {
  ADDED: 'added',
  REMOVED: 'removed',
  UNCHANGED: 'unchanged',
  CHANGED: 'changed'
};

const ITEM_INFO_FETCH_CEILING_COUNT = 1000;

class ItemChanges extends Component {
  static defaultProps = {};

  static propTypes = {
    from: PropTypes.object.isRequired,
    to: PropTypes.object.isRequired
  };

  componentDidMount() {
    const { from, to } = this.props;
    const fetchItemsInfo =
      from.itemsCount < ITEM_INFO_FETCH_CEILING_COUNT && to.itemsCount < ITEM_INFO_FETCH_CEILING_COUNT;

    this.props.fetchItems(from.termId, from.revisionId, fetchItemsInfo);
    this.props.fetchItems(to.termId, to.revisionId, fetchItemsInfo);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { from: prevFrom, to: prevTo } = prevProps;
    const { from, to } = this.props;

    if (prevFrom.termId !== from.termId || prevFrom.revisionId !== from.revisionId) {
      this.props.fetchItems(from.termId, from.revisionId, from.itemsCount < ITEM_INFO_FETCH_CEILING_COUNT);
    }
    if (prevTo.termId !== to.termId || prevTo.revisionId !== to.revisionId) {
      this.props.fetchItems(to.termId, to.revisionId, to.itemsCount < ITEM_INFO_FETCH_CEILING_COUNT);
    }
  }

  getDiffInformation(fromItems, toItems, from, to) {
    const diffInfos = [];

    // added
    diffInfos.push(
      ...Object.values(toItems)
        .filter(toItem => !fromItems[toItem.sku])
        .map(item => ({ sku: item.sku, from: null, to: item, diffStatus: DiffStatus.ADDED }))
    );
    // removed
    diffInfos.push(
      ...Object.values(fromItems)
        .filter(fromItem => !toItems[fromItem.sku])
        .map(item => ({ sku: item.sku, from: item, to: null, diffStatus: DiffStatus.REMOVED }))
    );
    const { changed, unchanged } = Object.values(fromItems).reduce(
      (map, fromItem) => {
        const toItem = toItems[fromItem.sku];
        // if the sku doesn't exist, skip.
        if (!toItem) {
          return map;
        }

        // if there were no changes to the price model and no changes to the modifiers.
        if (isEqual(fromItem, toItem) && isEqual(from.modifiers, to.modifiers)) {
          map.unchanged.push({ sku: fromItem.sku, from: fromItem, to: toItem, diffStatus: DiffStatus.UNCHANGED });
        } else {
          map.changed.push({ sku: fromItem.sku, from: fromItem, to: toItem, diffStatus: DiffStatus.CHANGED });
        }
        return map;
      },
      { changed: [], unchanged: [] }
    );
    diffInfos.push(...changed);
    diffInfos.push(...unchanged);

    return diffInfos;
  }

  render() {
    const { fromItems, toItems, from, to, isItemsLoading, fromItemsLoaded, toItemsLoaded } = this.props;
    const diffInfos = this.getDiffInformation(fromItems, toItems, from, to);

    const isLoading = isItemsLoading || !fromItemsLoaded || !toItemsLoaded;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Loading />
        ) : (
          <ItemsDiffDisplay from={from} to={to} diffInfos={diffInfos} fromItems={fromItems} toItems={toItems} />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { from, to } = ownProps;
  return {
    fromItems: termSelectors.getTermRevisionItemsWithSkuInfo(state, from.termId, from.revisionId),
    toItems: termSelectors.getTermRevisionItemsWithSkuInfo(state, to.termId, to.revisionId),
    isItemsLoading: termSelectors.getIsTermRevisionItemsLoading(state),
    itemsLoadingFailed: termSelectors.getHasTermRevisionItemsLoadingFailed(state),
    fromItemsLoaded: termSelectors.getTermRevisionItemsLoaded(state, from.termId, from.revisionId),
    toItemsLoaded: termSelectors.getTermRevisionItemsLoaded(state, to.termId, to.revisionId)
  };
};

const mapDispatchToProps = {
  fetchItems: termActions.fetchTermRevisionItems
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ItemChanges);
